import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { useAssignments } from '../../hooks/useAssignments';
import { useExams } from '../../hooks/useExams';
import { academicService } from '../../services/academicService';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, 
  BookOpen, 
  Calendar, 
  FileText,
  Award,
  Clock,
  AlertCircle
} from 'lucide-react';

const TeacherDashboard = () => {
  const { user } = useAuth();
  const { assignments, loading: assignmentsLoading } = useAssignments({ scope: 'teacher', limit: 5 });
  const { exams, loading: examsLoading } = useExams({ scope: 'teacher', timeframe: 'upcoming', limit: 3 });
  const [classes, setClasses] = React.useState([]);
  const [classSchedule, setClassSchedule] = React.useState([]);
  const [studentAlerts, setStudentAlerts] = React.useState([]);
  const [recentGrades, setRecentGrades] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const run = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const clsRes = await academicService.getClasses({ teacherId: user.id });
        // Ensure classes is always an array
        const classesData = clsRes.data?.data || [];
        setClasses(Array.isArray(classesData) ? classesData : []);
        
        // Use my-schedule endpoint instead of teacher-specific endpoint
        const ttRes = await academicService.getMyTimetable({ today: true });
        const timetableData = ttRes.data?.data?.timetables || ttRes.data?.data || [];
        setClassSchedule(Array.isArray(timetableData) ? timetableData : []);
      } catch (e) {
        console.error('Error fetching dashboard data:', e);
        setClasses([]);
        setClassSchedule([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [user?.id]);

  // Ensure classes is always an array before calling reduce
  const totalStudents = Array.isArray(classes) ? classes.reduce((sum, c) => sum + (c.studentCount || 0), 0) : 0;
  const activeClasses = classes.length;
  const pendingAssignments = (assignments || []).filter(a => (a.status || '').toLowerCase() === 'open').length;
  const upcomingExams = exams || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'current': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (assignmentsLoading || examsLoading || loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="w-full">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Teacher Dashboard
          </h1>
          <p className="text-gray-600">
            Welcome back, {user?.firstName || 'Teacher'}! Manage your classes and students.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Users className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Total Students</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalStudents}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Active Classes</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{activeClasses}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Open Assignments</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{pendingAssignments}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Upcoming Exams</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{upcomingExams.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Recent Assignments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Recent Assignments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {(assignments || []).map((assignment) => (
                  <div key={assignment.id} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-medium text-gray-900 truncate">
                        {assignment.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {(assignment.subjectName || assignment.subject)} • {(assignment.className || assignment.class)}
                      </p>
                      <p className="text-xs text-gray-500">Due: {assignment.dueDate}</p>
                    </div>
                    <div className="text-right ml-2">
                      {assignment.submittedCount != null && assignment.totalSubmissions != null && (
                        <>
                          <div className="text-xs sm:text-sm font-medium text-gray-900">
                            {assignment.submittedCount}/{assignment.totalSubmissions}
                          </div>
                          <div className="w-16 bg-gray-200 rounded-full h-2 mt-1">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${Math.round((assignment.submittedCount / Math.max(assignment.totalSubmissions,1)) * 100)}%` }}
                            ></div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Exams */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Upcoming Exams
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {upcomingExams.map((exam) => (
                  <div key={exam.id} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-medium text-gray-900">
                        {exam.subjectName || exam.subject}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600">{exam.className || exam.class}</p>
                      <p className="text-xs text-gray-500">{exam.examDate} at {exam.startTime}</p>
                    </div>
                    <div className="text-right ml-2">
                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                        {exam.duration} mins
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Today's Schedule */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Today's Schedule
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {classSchedule.map((schedule) => (
                  <div key={schedule.id} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-medium text-gray-900">
                        {schedule.subject}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600">{schedule.class}</p>
                      <p className="text-xs text-gray-500">{schedule.room}</p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="text-xs sm:text-sm text-gray-600">{schedule.time}</p>
                      <Badge className={`text-xs ${getStatusColor(schedule.status)}`}>
                        {schedule.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Student Alerts */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Student Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {studentAlerts.map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-medium text-gray-900">
                        {alert.student}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600">{alert.issue}</p>
                      <p className="text-xs text-gray-500">{alert.class} • {alert.subject}</p>
                    </div>
                    <Badge className={`text-xs ${getSeverityColor(alert.severity)}`}>
                      {alert.severity}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Grades */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <Award className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Recent Grades
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {recentGrades.map((grade, index) => (
                  <div key={index} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-medium text-gray-900">
                        {grade.student}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600">{grade.assignment}</p>
                      <p className="text-xs text-gray-500">{grade.subject}</p>
                    </div>
                    <div className="text-right ml-2">
                      <Badge className="bg-green-100 text-green-800 text-xs">
                        {grade.grade}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">{grade.percentage}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <button className="p-3 sm:p-4 border rounded-lg hover:bg-gray-50 text-center transition-colors">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-blue-600" />
                  <p className="text-xs sm:text-sm font-medium">Manage Students</p>
                </button>
                <button className="p-3 sm:p-4 border rounded-lg hover:bg-gray-50 text-center transition-colors">
                  <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-green-600" />
                  <p className="text-xs sm:text-sm font-medium">View Classes</p>
                </button>
                <button className="p-3 sm:p-4 border rounded-lg hover:bg-gray-50 text-center transition-colors">
                  <FileText className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-purple-600" />
                  <p className="text-xs sm:text-sm font-medium">Grade Assignments</p>
                </button>
                <button className="p-3 sm:p-4 border rounded-lg hover:bg-gray-50 text-center transition-colors">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-yellow-600" />
                  <p className="text-xs sm:text-sm font-medium">Schedule Exam</p>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
  );
};

export default TeacherDashboard;
